// A selected option must never submit the previous variant while its HTML loads.
(() => {
  window.Eurus = window.Eurus || {};
  if (window.Eurus.variantSelection) return;
  const requests = new WeakMap();
  const errorElements = new WeakMap();
  const formStates = new WeakMap();
  const buttonStates = new WeakMap();
  let nextRequest = 0;
  const labels = () => document.documentElement.lang.startsWith('it')
    ? { error: 'Impossibile aggiornare l’opzione. Riprova prima di aggiungere il prodotto.', retry: 'Riprova', busy: 'Aggiornamento dell’opzione in corso. Attendi un momento.' }
    : { error: 'The option could not be updated. Please retry before adding this product.', retry: 'Retry', busy: 'The option is updating. Please wait a moment.' };

  function formsFor(element, sectionId, cloneSectionId) {
    const forms = new Set();
    [sectionId, cloneSectionId].filter(Boolean).forEach(id => {
      ['product-form-', 'product-form-installment-', 'product-form-sticky-'].forEach(prefix => {
        const form = document.getElementById(prefix + id);
        if (form) forms.add(form);
      });
    });
    // Card/quick-option IDs may be cloned, so include the local form as well.
    const container = element.closest('.choose-options-content, .choose-options, .card-product, .x-product-bundle-data');
    if (container) container.querySelectorAll('form[data-type="add-to-cart-form"], form[id^="product-form-"]').forEach(form => forms.add(form));
    return [...forms];
  }

  function setButtonDisabled(button, disabled) {
    const state = buttonStates.get(button);
    if (state) state.disabled = !!disabled;
    button.disabled = state ? true : !!disabled;
  }

  function lock(form, token) {
    let state = formStates.get(form);
    if (!state) {
      state = { buttons: [], payments: [] };
      formStates.set(form, state);
    }
    state.token = token;
    form.dataset.variantPending = 'true';
    delete form.dataset.variantError;
    form.setAttribute('aria-busy', 'true');
    form.querySelectorAll('button[name="add"], button[type="submit"], .add_to_cart_button').forEach(button => {
      if (!buttonStates.has(button)) {
        buttonStates.set(button, { disabled: button.disabled });
        state.buttons.push(button);
      }
      button.disabled = true;
    });
    form.querySelectorAll('.payment-button-container, .shopify-payment-button, shopify-accelerated-checkout').forEach(payment => {
      if (!state.payments.some(item => item.element === payment)) {
        state.payments.push({ element: payment, inert: payment.inert, pointerEvents: payment.style.pointerEvents, opacity: payment.style.opacity });
      }
      payment.inert = true;
      payment.style.pointerEvents = 'none';
      payment.style.opacity = '0.5';
    });
  }

  function finish(form, token, success) {
    const state = formStates.get(form);
    if (!state || state.token !== token) return;
    delete form.dataset.variantPending;
    form.removeAttribute('aria-busy');
    if (!success) {
      form.dataset.variantError = 'true';
      return; // A failed selection stays unavailable until a successful retry.
    }
    delete form.dataset.variantError;
    state.buttons.forEach(button => {
      const previous = buttonStates.get(button);
      if (previous) button.disabled = previous.disabled;
      buttonStates.delete(button);
    });
    state.payments.forEach(item => {
      item.element.inert = item.inert;
      item.element.style.pointerEvents = item.pointerEvents;
      item.element.style.opacity = item.opacity;
    });
    formStates.delete(form);
  }

  function showError(key, anchor, forms, retry) {
    let box = errorElements.get(key);
    if (!box || !box.isConnected) {
      box = document.createElement('div');
      box.className = 'variant-request-error';
      box.setAttribute('role', 'alert');
      box.style.cssText = 'margin:12px 0;padding:12px;border:1px solid currentColor;color:#a42626;';
      const target = anchor && anchor.isConnected ? anchor : forms[0];
      if (target) target.insertAdjacentElement('afterend', box);
      errorElements.set(key, box);
    }
    box.replaceChildren();
    const text = document.createElement('span');
    text.textContent = labels().error + ' ';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = labels().retry;
    button.style.cssText = 'text-decoration:underline;font-weight:600;';
    button.addEventListener('click', retry, { once: true });
    box.append(text, button);
    box.hidden = false;
  }

  function run(owner, options) {
    // Alpine creates a new merged `this` proxy for separate expressions.
    // The selector DOM node remains stable across those calls and innerHTML
    // updates, so it owns request identity and error UI instead of the proxy.
    const key = options.anchor || owner;
    if (options.anchor && !options.anchor.isConnected) return Promise.resolve(false);
    const previous = requests.get(key);
    if (previous) previous.controller.abort();
    const controller = new AbortController();
    const token = ++nextRequest;
    const state = { token, controller };
    requests.set(key, state);
    const initialForms = options.forms();
    initialForms.forEach(form => lock(form, token));
    if (errorElements.has(key)) errorElements.get(key).hidden = true;
    const current = () => requests.get(key) === state;
    const attached = () => {
      if (options.anchor && !options.anchor.isConnected) return false;
      const forms = options.forms();
      return initialForms.every(form => form.isConnected && forms.includes(form));
    };
    const releaseDetached = () => initialForms.forEach(form => finish(form, token, true));
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Variant request timed out'));
      }, 15000);
    });
    const promise = Promise.race([Promise.resolve().then(() => options.load(controller.signal)), timeout])
      .then(data => {
        if (!current()) return false;
        // QuickView reuses section/form IDs for another product. A response
        // from the removed selector must never update that new DOM instance.
        if (!attached()) { releaseDetached(); return false; }
        options.apply(data);
        initialForms.forEach(form => finish(form, token, true));
        return true;
      })
      .catch(error => {
        if (!current()) return false;
        if (!attached()) { releaseDetached(); return false; }
        const currentForms = options.forms();
        currentForms.forEach(form => lock(form, token));
        new Set([...initialForms, ...currentForms]).forEach(form => finish(form, token, false));
        showError(key, options.anchor, currentForms, () => run(owner, options));
        console.warn('Variant selection could not be updated:', error);
        return false;
      })
      .finally(() => {
        clearTimeout(timer);
        if (current()) {
          requests.delete(key);
          if (options.onFinish) options.onFinish();
        }
      });
    return promise;
  }

  const blocked = form => !!form && (form.dataset.variantPending === 'true' || form.dataset.variantError === 'true');
  function guard(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    const form = target.form || target.closest('form');
    if (!blocked(form)) return;
    if (event.type === 'submit' || target.closest('button[name="add"], button[type="submit"], .add_to_cart_button, .payment-button-container, .shopify-payment-button, shopify-accelerated-checkout')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
  document.addEventListener('submit', guard, true);
  document.addEventListener('click', guard, true);
  window.Eurus.variantSelection = { formsFor, setButtonDisabled, run, blocked, labels };
})();
